"use client";

import React, { useCallback, useEffect, useSyncExternalStore } from 'react';
import Link from 'next/link';
import { Download, Check } from 'lucide-react';
import { PlayerSeek, PlayerTime } from '@/components/ui/audiocn/player';
import { TPlay } from '@/components/ui/tls/t-play';
import { useAudioPlayerContext } from '@/contexts/audio-player-context';
import { useAudioUrl } from '@/hooks/useAudioUrl';
import { type Sound } from '@/lib/supabase';
import { soundUrl } from '@/lib/slugify';
import { playbackAudioSource } from '@/lib/sound-audio-source';
import { useIntentPreload } from '@/hooks/useIntentPreload';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useCardDownload } from '@/components/ui/sound-card/use-card-download';
import { formatSoundDuration } from '@/lib/ranked-sounds';
import { categoryLabel } from '@/lib/category-taxonomy';
import { downloadCountAriaLabel } from '@/lib/format-count';
import { previewFailureMessage } from '@/lib/state-copy';
import { clearRowError, reportRowError } from '@/lib/row-error-store';

// Hydration-safe mounted check
const subscribe = () => () => {};
const getSnapshot = () => true;
const getServerSnapshot = () => false;

interface BlogSoundEmbedProps {
  sound: Sound;
  className?: string;
}

/**
 * Compact inline audio player for embedding in blog content, on the same
 * AudioCN parts as a sound row: the play disc, a strip drawn from stored
 * peaks (a still SVG until this embed is the current sound, then the seek
 * slider), and the time readout in place of the duration while current.
 */
export const BlogSoundEmbed: React.FC<BlogSoundEmbedProps> = ({
  sound,
  className = '',
}) => {
  // Hydration-safe mounted check using useSyncExternalStore
  const mounted = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  // Global audio player context
  const globalPlayer = useAudioPlayerContext();

  const isCurrent = globalPlayer.currentSound?.id === sound?.id;
  const isThisSoundPlaying = isCurrent && globalPlayer.isPlaying;
  const isThisSoundLoading = isCurrent && globalPlayer.isLoading;
  // The catalog length first, so the readout does not change on Play.
  const seekDuration = sound?.duration || (isCurrent ? globalPlayer.duration : 0) || 0;
  const currentTime = isCurrent ? globalPlayer.currentTime || 0 : 0;
  // The strip is hidden under Tailwind's sm (40rem), so there it never mounts a
  // canvas; the server and phones keep the still SVG.
  const stripShown = useMediaQuery('(min-width: 40rem)', false);
  const liveStrip = isCurrent && stripShown;

  // Playback URL: the compressed preview when there is one, else the WAV
  // through its server-resolved audioUrl (TES-831) when present, matching
  // what the global player resolves. The file itself is fetched on
  // hover/press intent only: an embed that is read past costs nothing.
  const playbackSource = playbackAudioSource(sound);
  const { audioUrl } = useAudioUrl(playbackSource.fileKey, playbackSource.resolvedUrl);
  const intentHandlers = useIntentPreload(sound?.id, audioUrl);

  // Play/Pause handler
  // eslint-disable-next-line react-doctor/no-event-handler -- handlePlayPause is a direct DOM event handler; naming is correct
  const handlePlayPause = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!mounted) return;
    // Loading counts as "engaged" so a second activation during the load
    // cancels it rather than re-issuing play() — the control is no longer
    // `disabled` while loading (TES-1581), matching the detail-page player.
    if (isThisSoundPlaying || isThisSoundLoading) {
      globalPlayer.pause();
    } else {
      clearRowError();
      globalPlayer.play(sound);
    }
  }, [isThisSoundPlaying, isThisSoundLoading, globalPlayer, sound, mounted]);

  const { isDownloading, downloadDone, downloadError: errorNote, handleCardDownload } = useCardDownload(sound, false, 'blog_embed');
  // Preview and download failures share the rows' one note slot
  // (lib/row-error-store.ts), so a failure here reads the same as in the library.
  const playerError = isCurrent ? globalPlayer.error : null;
  useEffect(() => {
    if (playerError && sound?.id) reportRowError(sound.id, previewFailureMessage(playerError));
  }, [playerError, sound?.id]);
  // Duration is hidden under 1 s, where the stored value is clipped or unmeasured.
  const duration = formatSoundDuration(sound?.duration);
  const category = sound?.category ? categoryLabel(sound.category) : null;
  const downloads = sound ? downloadCountAriaLabel(sound.downloads ?? 0) : null;

  if (!sound || !sound.id) {
    return null;
  }

  return (
    <div
      data-sound-embed
      className={`
        group relative flex flex-wrap items-center gap-3 py-3
        border-y border-[color:var(--tls-border-subtle)]
        transition-[background-color,border-color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]
        hover:border-[color:color-mix(in_srgb,var(--tls-brand)_28%,var(--tls-border-subtle))]
        ${isThisSoundPlaying ? 'border-[color:color-mix(in_srgb,var(--tls-brand)_34%,var(--tls-border-subtle))] bg-[color-mix(in_srgb,var(--tls-brand)_8%,var(--tls-surface))]' : ''}
        ${className}
      `}
    >
      {/* The intent handlers sit on a wrapper so a hover or press buffers
          this sound before Play, as on a sound row. */}
      <span className="flex shrink-0" {...intentHandlers}>
        <TPlay
          playing={isThisSoundPlaying || isThisSoundLoading}
          loading={isThisSoundLoading}
          title={sound.title}
          size={44}
          onClick={handlePlayPause}
        />
      </span>

      {/* Title, then the same facts line as a library row: category,
          duration, installs with its unit. The title wraps, never truncates. */}
      <div className="flex-1 min-w-0">
        <Link prefetch={false}
          href={soundUrl(sound)}
          target="_blank"
          rel="noopener noreferrer"
          className="
            block text-pretty [overflow-wrap:anywhere]
            text-[var(--tls-fg-strong)] font-semibold text-base hover:text-[var(--tls-brand-ink)]
            transition-colors
          "
        >
          {sound.title}
        </Link>
        <p className="m-0 mt-0.5 flex flex-wrap gap-x-3 text-sm font-semibold tabular-nums text-[var(--tls-fg-muted)]">
          {category && <span>{category}</span>}
          {isCurrent && seekDuration > 0 ? (
            <span>
              <PlayerTime inheritFont currentTime={currentTime} duration={seekDuration} />
              {' / '}
              <PlayerTime inheritFont className="min-w-0" type="duration" duration={seekDuration} />
            </span>
          ) : (
            duration && <span>{duration}</span>
          )}
          {downloads && <span>{downloads}</span>}
        </p>
      </div>

      {/* Stored peaks only. At rest, and wherever the strip is hidden, it is
          the server-drawn SVG with no canvas and no frame loop; the current
          embed mounts the canvas and becomes the "Seek <title>" slider. */}
      <div className="hidden sm:block w-32 flex-shrink-0">
        <PlayerSeek
          // Remounting on the switch keeps the SVG up until the new canvas has
          // painted, so the strip never blanks for a frame.
          key={liveStrip ? 'live' : 'still'}
          className="h-11"
          peaks={sound.waveform_data}
          duration={seekDuration}
          currentTime={currentTime}
          title={sound.title}
          decimals={1}
          still={!liveStrip}
          interactive={liveStrip && seekDuration > 0}
          playhead={isCurrent}
          onSeekCommit={globalPlayer.seek}
        />
      </div>

      {/* Download: the same flow as a sound card (count, tracking, file). */}
      <button
        type="button"
        onClick={handleCardDownload}
        aria-busy={isDownloading || undefined}
        aria-label={`Download ${sound.title}`}
        className="
          flex flex-shrink-0 min-h-[44px] min-w-[44px] items-center justify-center gap-1.5
          rounded-[var(--radius-pill)] px-2 text-sm font-semibold text-[var(--tls-fg-muted)]
          transition-colors duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]
          hover:bg-[var(--tls-surface-elevated)] hover:text-[var(--tls-brand-ink)]
          aria-[busy=true]:opacity-50 aria-[busy=true]:cursor-wait
        "
      >
        {downloadDone ? (
          <Check className="size-4" aria-hidden="true" />
        ) : (
          <Download className="size-4" aria-hidden="true" />
        )}
        {/* Both labels share one grid cell so the button keeps its width when it flips. */}
        <span className="hidden sm:inline-grid">
          <span className={`col-start-1 row-start-1 ${downloadDone ? 'invisible' : ''}`}>Download</span>
          <span className={`col-start-1 row-start-1 ${downloadDone ? '' : 'invisible'}`}>Saved</span>
        </span>
      </button>

      {/* A failure is said where it happened, in words (§09): the same
          sentence the live region carries. Indented to the title's edge. */}
      {errorNote && (
        <p data-testid="sound-embed-error" className="m-0 basis-full pl-14 text-sm font-bold text-[var(--tls-fg-strong)]">
          {errorNote}
        </p>
      )}
    </div>
  );
};

/**
 * Loading skeleton for BlogSoundEmbed
 */
export const BlogSoundEmbedSkeleton: React.FC<{ className?: string }> = ({ className = '' }) => {
  return (
    <div className={`flex items-center gap-3 py-3 border-y border-[color:var(--tls-border-subtle)] ${className}`}>
      <div className="size-11 rounded-[var(--radius-pill)] bg-[var(--tls-surface-elevated)]" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-32 rounded-[var(--ls-radius-0)] bg-[var(--tls-surface-elevated)]" />
        <div className="h-3 w-20 rounded-[var(--ls-radius-0)] bg-[color-mix(in_srgb,var(--tls-surface-elevated)_72%,transparent)]" />
      </div>
      <div className="hidden sm:block w-32 h-11 rounded-[var(--ls-radius-0)] bg-[color-mix(in_srgb,var(--tls-surface-elevated)_72%,transparent)]" />
      <div className="w-10 h-4 rounded-[var(--ls-radius-0)] bg-[color-mix(in_srgb,var(--tls-surface-elevated)_72%,transparent)]" />
    </div>
  );
};
